import type { Skill, SkillHabitItem } from '../services/skillService'
import type { Habit } from '../services/habitService'
import { levelWord } from './skillSections'
import { CADENCE_OPTIONS, LEVEL_OPTIONS } from './skillOptions'
import { clockWithZone, zoneAbbrev } from './taskForm'

/**
 * Words for a skill's level, cadence and practice (Skills UX pass 2026-09-06).
 * One place, so the list row, the detail summary line and the rail say the
 * same thing. Every clock time names its zone.
 */

/** "Beginner → Proficient" · "Beginner" · "Aiming for Proficient" · null. */
export function levelWords(s: Pick<Skill, 'current_level' | 'target_level'>): string | null {
  const cur = levelWord(s.current_level), tgt = levelWord(s.target_level)
  if (cur && tgt) return cur === tgt ? cur : `${cur} → ${tgt}`
  if (cur) return cur
  if (tgt) return `Aiming for ${tgt}`
  return null
}

/** What a rung means, in plain words. */
export function capabilityOf(level: string | null | undefined): string | null {
  return LEVEL_OPTIONS.find((o) => o.value === level)?.capability || null
}

/** "A few times a week" for a known cadence; the person's own words otherwise. */
export function cadenceWords(v: string | null | undefined): string | null {
  if (!v) return null
  const known = CADENCE_OPTIONS.find((o) => o.value === v)
  if (known) return known.label
  const w = String(v).replace(/_/g, ' ').trim()
  return w ? w.charAt(0).toUpperCase() + w.slice(1) : null
}

/** "Tue, Sep 8 at 6:30 PM PDT" · "Tue, Sep 8" · null, from the board's next thing (a task's
 *  slot is a local clock in the person's zone; a habit's next time is an instant). */
export interface NextLike { date?: string | null; time?: string | null; at?: string | null }

/** Shared by the skill and goal surfaces (one function, 2026-09-12 — the goal
 *  pages carried a copy that had drifted). */
export function nextWhenWords(next: NextLike | null | undefined, tz?: string | null): string | null {
  if (!next?.date) return null
  const [y, m, d] = next.date.split('-').map(Number)
  // "today" / "tomorrow" / "past its time" as the task and habit rows say it
  // (North Star rule 2, 2026-09-12): the skill row said "Sat, Sep 12 at 8:00
  // PM PDT" on Sep 12, and a goal's next read "Thu, Sep 10" two days late.
  const now = new Date()
  const base = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const offset = Math.round((new Date(y, m - 1, d).getTime() - base.getTime()) / 86_400_000)
  const dated = new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
  const day = offset === 0 ? 'today' : offset === 1 ? 'tomorrow' : offset < 0 ? `past its time · ${dated}` : dated
  if (next.time) return `${day} at ${clockWithZone(next.time, tz)}`
  if (next.at) {
    const at = new Date(next.at)
    if (!Number.isNaN(at.getTime())) return `${day} at ${at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })} ${zoneAbbrev(tz, at)}`
  }
  return day
}

/** A habit under a skill, in the shape the habit words read (nulls dropped). */
export function habitScheduleInput(h: SkillHabitItem): Parameters<typeof import('./habitWords').scheduleWords>[0] {
  return {
    rrule: h.rrule ?? undefined, scheduled_times_local: h.scheduled_times_local ?? undefined,
    preferred_days: (h.preferred_days ?? undefined) as Habit['preferred_days'], preferred_start_time: h.preferred_start_time ?? undefined,
    target_cadence: (h.target_cadence ?? undefined) as Habit['target_cadence'], timezone: h.timezone ?? undefined,
  }
}
export function habitReminderInput(h: SkillHabitItem): Parameters<typeof import('./habitWords').reminderWords>[0] {
  return {
    nag_enabled: h.nag_enabled ?? undefined, nag_window_minutes: h.nag_window_minutes ?? undefined, nag_interval_minutes: h.nag_interval_minutes ?? undefined,
    nag_max_per_window: h.nag_max_per_window ?? undefined, nag_style: (h.nag_style ?? undefined) as Habit['nag_style'],
    follow_through_mode: (h.follow_through_mode ?? undefined) as Habit['follow_through_mode'],
  }
}
